/**
 * UAT Question Bank - Reusable Questions for User Acceptance Testing
 * 
 * These questions are designed to capture structured feedback during UAT sessions.
 * They integrate with the existing feedback system and question bank infrastructure.
 * 
 * NOTE: Uses existing backend enum values (lowercase) for compatibility:
 * - Categories: delivery (step completion), technical (issues), communication (usability)
 * - Context: project (for UAT testing)
 * 
 * The backend Python enums use lowercase values, so we cast to `any` for API compatibility.
 */

import { QuestionCreate, QuestionType, QuestionCategory } from '@/services/api/questionBankService'

// Backend uses lowercase enum values
type BackendQuestionType = 'text' | 'rating_scale' | 'likert' | 'multiple_choice' | 'multi_select' | 'yes_no' | 'ranking'
type BackendQuestionCategory = 'communication' | 'leadership' | 'technical' | 'collaboration' | 'delivery' | 'innovation' | 'development' | 'values' | 'student_engagement'
type BackendFeedbackContext = 'matrix' | 'upward_360' | 'employee_requested' | 'peer' | 'project'

// Helper type that accepts backend lowercase values
interface UATQuestionCreate extends Omit<QuestionCreate, 'question_type' | 'category' | 'applicable_feedback_contexts'> {
  question_type: BackendQuestionType
  category: BackendQuestionCategory
  applicable_feedback_contexts: BackendFeedbackContext[]
}

// ==================== Common UAT Questions ====================

const UAT_QUESTIONS_RAW: UATQuestionCreate[] = [
  // Step Completion Questions - using 'delivery' category
  {
    question_code: 'UAT-STEP-COMPLETE',
    question_text: 'Were you able to complete this step successfully?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Select Yes if the step worked as expected, No if you encountered any issues.',
  },
  {
    question_code: 'UAT-STEP-BLOCKER',
    question_text: 'Was this step a blocker preventing you from continuing?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 2,
    helper_text: 'A blocker means you could not proceed to the next step at all.',
  },
  {
    question_code: 'UAT-PARTIAL-SUCCESS',
    question_text: 'If partially successful, which parts worked?',
    question_type: 'text',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 3,
    helper_text: 'Describe which parts of the step worked correctly.',
  },

  // Issue Reporting Questions - using 'technical' category
  {
    question_code: 'UAT-ISSUE-DESCRIPTION',
    question_text: 'Please describe the issue you encountered:',
    question_type: 'text',
    category: 'technical',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 10,
    helper_text: 'Provide as much detail as possible about what went wrong.',
  },
  {
    question_code: 'UAT-ISSUE-SEVERITY',
    question_text: 'How severe is this issue?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Critical - Cannot use the feature at all',
        'Major - Feature works but with significant problems',
        'Minor - Feature works but has small issues',
        'Cosmetic - Visual or text issues only'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 11,
    helper_text: 'Select the severity level that best describes the impact.',
  },
  {
    question_code: 'UAT-ERROR-MESSAGE',
    question_text: 'Did you see any error messages? If so, what did they say?',
    question_type: 'text',
    category: 'technical',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 12,
    helper_text: 'Copy the exact error message text if possible.',
  },
  {
    question_code: 'UAT-SCREENSHOT',
    question_text: 'Were you able to take a screenshot of the issue?',
    question_type: 'yes_no',
    category: 'technical',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 13,
    helper_text: 'If yes, please attach it via email to uat-support@eduvos.com',
  },

  // Usability Questions - using 'communication' category
  {
    question_code: 'UAT-EASE-OF-USE',
    question_text: 'How easy was this step to complete?',
    question_type: 'rating_scale',
    category: 'communication',
    question_config: {
      min: 1,
      max: 5,
      labels: {
        '1': 'Very Difficult',
        '2': 'Difficult',
        '3': 'Neutral',
        '4': 'Easy',
        '5': 'Very Easy'
      }
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 20,
    helper_text: 'Rate from 1 (very difficult) to 5 (very easy).',
  },
  {
    question_code: 'UAT-INSTRUCTIONS-CLEAR',
    question_text: 'Were the on-screen instructions clear?',
    question_type: 'rating_scale',
    category: 'communication',
    question_config: {
      min: 1,
      max: 5,
      labels: {
        '1': 'Strongly Disagree',
        '2': 'Disagree',
        '3': 'Neutral',
        '4': 'Agree',
        '5': 'Strongly Agree'
      }
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 21,
    helper_text: 'Rate how clear and understandable the instructions were.',
  },
  {
    question_code: 'UAT-CONFUSION-POINTS',
    question_text: 'What, if anything, was confusing about this step?',
    question_type: 'text',
    category: 'communication',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 22,
    helper_text: 'Describe any areas where you were unsure what to do.',
  },
  {
    question_code: 'UAT-SUGGESTIONS',
    question_text: 'Do you have any suggestions for improving this step?',
    question_type: 'text',
    category: 'development',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 23,
    helper_text: 'Share any ideas that would make this feature better.',
  },

  // Overall Test Case Questions
  {
    question_code: 'UAT-OVERALL-PASS',
    question_text: 'Overall, did this test case PASS or FAIL?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: ['PASS', 'FAIL', 'PARTIAL']
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 100,
    helper_text: 'PASS if all steps completed, FAIL if blocked, PARTIAL if some steps worked.',
  },
  {
    question_code: 'UAT-TIME-TAKEN',
    question_text: 'Approximately how long did this test case take?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Less than 2 minutes',
        '2-5 minutes',
        '5-10 minutes',
        '10-15 minutes',
        'More than 15 minutes'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 101,
    helper_text: 'Estimate the time spent on this test case.',
  },
  {
    question_code: 'UAT-ADDITIONAL-NOTES',
    question_text: 'Any additional notes or observations?',
    question_type: 'text',
    category: 'development',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 102,
    helper_text: 'Capture any other thoughts about this test case.',
  },
]

// ==================== Step-Specific Questions ====================

// Authentication-specific questions
const AUTH_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-AUTH-SSO-WORKED',
    question_text: 'Did the Microsoft SSO login work correctly?',
    question_type: 'yes_no',
    category: 'technical',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Yes if you were redirected to Microsoft and back successfully.',
  },
  {
    question_code: 'UAT-AUTH-REDIRECT-CORRECT',
    question_text: 'Were you redirected to the correct page after login?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Team Member Journey (returning user)',
        'Yes - Onboarding (first-time user)',
        'No - Wrong page',
        'No - Error occurred'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'First-time users should go to Onboarding, returning users to Team Member Journey.',
  },
  {
    question_code: 'UAT-AUTH-PROFILE-LOADED',
    question_text: 'Did your profile information load correctly in the header?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Your name should appear in the top-right header area.',
  },
]

// Onboarding-specific questions
const ONBOARDING_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-OB-STEP-FLOW',
    question_text: 'Did the onboarding steps flow in the correct order?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Expected: Welcome → F&O Confirmation → Profile Completion → People Leader Orientation (people leaders only) → Objective Planning',
  },
  {
    question_code: 'UAT-OB-PROGRESS-VISIBLE',
    question_text: 'Was the progress indicator visible and updating correctly?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'The progress bar at the top should show your current step and percentage complete.',
  },
  {
    question_code: 'UAT-OB-FO-DATA-ACCURATE',
    question_text: 'Was your F&O data (name, job title, department, people leader) accurate?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - All data was correct',
        'Mostly - Minor issues only',
        'No - Some data was wrong',
        'No - Most data was wrong'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Compare with your actual employment details.',
  },
  {
    question_code: 'UAT-OB-PROFILE-SAVE',
    question_text: 'Were you able to save your profile information (qualifications, career history)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 4,
    helper_text: 'You should see a success message when your profile is saved.',
  },
  {
    question_code: 'UAT-OB-INTERACTIVE-TOUR',
    question_text: 'Did the interactive demo tour work on the Team Member Journey page?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Tour launched automatically',
        'Yes - Clicked the demo button to start',
        'No - Tour did not appear',
        'No - Tour had errors'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'The tour uses popups to highlight features on the page.',
  },
]

// ==================== Team Member Journey Questions ====================

// Objective Setting Questions (TC-020)
const OBJECTIVE_SETTING_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-OBJ-WIZARD-STEPS',
    question_text: 'Did the 5-step objective wizard flow correctly?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Expected steps: Basic Information → Timeline → KPIs → SMART Analysis → Review',
  },
  {
    question_code: 'UAT-OBJ-KPA-SELECT',
    question_text: 'Could you select a Key Performance Area (KPA) from the dropdown?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'KPA options include Operating/Financial, Internal Processes, Culture/People, Customer/Stakeholder.',
  },
  {
    question_code: 'UAT-OBJ-STRATEGIC-CASCADE',
    question_text: 'Did the strategic cascade (L1→L2→L3) filter correctly when selecting levels?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - L2 options filtered based on L1, L3 filtered based on L2',
        'Partially - Some filtering worked',
        'No - Options were not filtered correctly',
        'Did not use strategic cascade'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Selecting a Strategic Objective (L1) should filter the Initiative (L2) options.',
  },
  {
    question_code: 'UAT-OBJ-AI-SUGGESTIONS',
    question_text: 'Did the AI Strategic Suggestions feature work?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - AI provided helpful alignment suggestions',
        'Yes - But suggestions were not relevant',
        'No - AI feature did not load',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'The AI Strategic Suggestions button analyses your objective and suggests strategic alignment.',
  },
  {
    question_code: 'UAT-OBJ-KPI-ADD',
    question_text: 'Could you add KPIs with targets to your objective?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 5,
    helper_text: 'You should be able to add multiple KPIs with name, target, unit, and frequency.',
  },
  {
    question_code: 'UAT-OBJ-SMART-ANALYSIS',
    question_text: 'Did the SMART Analysis generate a score and feedback?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Saw score and feedback for all 5 SMART criteria',
        'Partial - Some criteria missing feedback',
        'No - SMART analysis did not load',
        'Error occurred during analysis'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 6,
    helper_text: 'SMART = Specific, Measurable, Achievable, Relevant, Time-bound.',
  },
  {
    question_code: 'UAT-OBJ-SUBMIT-STATUS',
    question_text: 'After submission, did the objective show "Pending Approval" status?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 7,
    helper_text: 'Team member-proposed objectives require people leader approval.',
  },
]

// View Objectives Questions (TC-021)
const VIEW_OBJECTIVES_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-VIEW-KPA-CARDS',
    question_text: 'Were your KPA cards displayed with progress indicators?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Each KPA should show circular progress %, status badge, objectives and KPI counts.',
  },
  {
    question_code: 'UAT-VIEW-KPA-WORKSPACE',
    question_text: 'Did clicking a KPA open the KPA workspace with tabs?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Expected tabs: Objectives & KPIs, Evidence.',
  },
  {
    question_code: 'UAT-VIEW-KPI-VALUES',
    question_text: 'Did KPI cards show current vs target values correctly?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - All values displayed correctly',
        'Partial - Some values missing',
        'No - Values not displayed',
        'No KPIs to view'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'KPIs should show current value, target, progress bar, and status badge.',
  },
  {
    question_code: 'UAT-VIEW-YEAR-SELECTOR',
    question_text: 'Did the year selector work correctly?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Could switch years and data updated',
        'Yes - But legacy year showed error',
        'No - Year selector did not work',
        'Did not test year switching'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Previous years should show legacy F&O data in read-only mode.',
  },
  {
    question_code: 'UAT-VIEW-STRATEGIC-CONTEXT',
    question_text: 'Could you view the Strategic Context tab showing pillar alignment?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'Shows how objectives align to strategic pillars (L1→L2→L3).',
  },
]

// Add Evidence Questions (TC-022)
const ADD_EVIDENCE_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-EV-ADD-FROM-KPI',
    question_text: 'Could you add evidence from a specific KPI?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Clicking Add Evidence on a KPI should pre-select that KPI in the form.',
  },
  {
    question_code: 'UAT-EV-MEASUREMENT',
    question_text: 'Could you enter a new KPI measurement value with supporting document?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Evidence form should show Current Value, New Value input, and Target.',
  },
  {
    question_code: 'UAT-EV-FILE-UPLOAD',
    question_text: 'Did file upload work correctly?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - File uploaded successfully',
        'No - Upload failed with error',
        'No - Upload button not working',
        'Did not upload a file'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Supported file types include PDF, images, and documents.',
  },
  {
    question_code: 'UAT-EV-PROGRESS-UPDATE',
    question_text: 'After adding KPI evidence, did the KPI progress bar update?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 4,
    helper_text: 'The KPI current value and progress should reflect your new measurement.',
  },
  {
    question_code: 'UAT-EV-PERIOD-FILTER',
    question_text: 'Did evidence filter correctly by review period (Mid-Year/Year-End)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Evidence filtered by selected period',
        'No - Evidence not filtering correctly',
        'Did not test period filtering'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'Switching periods should show only evidence for that review period.',
  },
  {
    question_code: 'UAT-EV-EDIT-DELETE',
    question_text: 'Could you edit and delete evidence items?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Both edit and delete worked',
        'Edit worked, delete did not',
        'Delete worked, edit did not',
        'Neither worked',
        'Did not test these features'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 6,
    helper_text: 'You should be able to modify or remove evidence you have added.',
  },
]

// Performance Review Questions (TC-023)
const PERFORMANCE_REVIEW_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-REV-PERIOD-CARDS',
    question_text: 'Did you see all 4 check-in period cards (Q1, Mid-Year, Q3, Year-End)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Check-ins tab should show cards for all 4 check-in periods with status.',
  },
  {
    question_code: 'UAT-REV-PREPARE-BUTTON',
    question_text: 'Could you access "Prepare for 1:1 Check-In" on a scheduled check-in?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Self-reflection workspace opened',
        'No - Button was disabled',
        'No - Error when clicking',
        'No check-ins scheduled'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Prepare for 1:1 Check-In opens the self-reflection workspace.',
  },
  {
    question_code: 'UAT-REV-AI-TIER1',
    question_text: 'Did Tier 1 AI Review Preparation generate insights?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Generated helpful evidence-based insights',
        'Yes - But insights were not useful',
        'No - AI feature did not work',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 3,
    helper_text: 'Tier 1 AI analyses your evidence to generate review preparation insights.',
  },
  {
    question_code: 'UAT-REV-AI-TIER2',
    question_text: 'Did Tier 2 KPA Discussion Guide show suggested talking points?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Showed helpful per-KPA guidance',
        'Yes - But guidance was not relevant',
        'No - Discussion guide did not appear',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Tier 2 provides discussion guidance for each KPA.',
  },
  {
    question_code: 'UAT-REV-KPA-TABS',
    question_text: 'Did each KPA have the 3-tab structure (Objectives & KPIs, Evidence, Review Discussion)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 5,
    helper_text: 'Each KPA in self-review should have these three sub-tabs.',
  },
  {
    question_code: 'UAT-REV-DISCUSSION-FIELDS',
    question_text: 'Could you enter text in What Went Well, Challenges, and Actions fields?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 6,
    helper_text: 'The 3-step discussion structure for each KPA.',
  },
  {
    question_code: 'UAT-REV-SAVE-DRAFT',
    question_text: 'Could you save draft and return later?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 7,
    helper_text: 'Save Draft should preserve your entries for later completion.',
  },
  {
    question_code: 'UAT-REV-TIER34-HIDDEN',
    question_text: 'Were Tier 3 (Rating Assistant) and Tier 4 (Synthesis) hidden from you as a team member?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Did not see rating or synthesis features',
        'No - I could see rating options (should be hidden)',
        'Not sure'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 8,
    helper_text: 'Team members should NOT see Tier 3/4 AI features - these are people leader-only.',
  },
]

// IDP Management Questions (TC-025)
const IDP_MANAGEMENT_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-IDP-TABS-NAV',
    question_text: 'Did all My Personalised Growth tabs work (Career Aspirations, Strengths, Opportunities, Interventions, Overview)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'You should be able to navigate between all five tabs.',
  },
  {
    question_code: 'UAT-IDP-CAREER-OBJ',
    question_text: 'Could you set a Career Aspirations with the three types (Current Role, Expand Impact, Another Role)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Selected type and saved successfully',
        'Yes - But could not set long-term goal',
        'No - Career objective section not working',
        'Did not test career objective'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Career focus types: Current Role, Expand Impact, Another Role.',
  },
  {
    question_code: 'UAT-IDP-ADD-ITEMS',
    question_text: 'Could you add items to Strengths, Growth Opportunities, and Interventions?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Added items to all sections',
        'Partial - Some sections worked',
        'No - Could not add items',
        'Did not test adding items'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Each section should have an Add button to create new items.',
  },
  {
    question_code: 'UAT-IDP-KPA-LINK',
    question_text: 'Could you link My Personalised Growth items to KPAs?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - KPA selector showed my KPAs',
        'No - KPA selector was empty',
        'No - Could not link to KPAs',
        'Did not test KPA linking'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Strengths and Growth Opportunities can be linked to your assigned KPAs.',
  },
  {
    question_code: 'UAT-IDP-PENDING-STATUS',
    question_text: 'Did newly added items show "Pending People Leader Review" status?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 5,
    helper_text: 'Team member-created items require people leader approval.',
  },
  {
    question_code: 'UAT-IDP-MGR-FEEDBACK',
    question_text: 'Could you see people leader feedback/notes on approved or rejected items?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - People leader notes were visible',
        'No items have been reviewed yet',
        'No - Feedback not showing'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 6,
    helper_text: 'People leader feedback should be visible on reviewed items.',
  },
]

// ==================== People Leader Journey Questions ====================

// Team Overview Questions (TC-030)
const TEAM_OVERVIEW_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-TEAM-MEMBERS-VISIBLE',
    question_text: 'Were all your direct reports visible in the Team Overview?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Your direct reports from F&O should be displayed.',
  },
  {
    question_code: 'UAT-TEAM-SUMMARY-STATS',
    question_text: 'Did summary statistics show correctly (team size, progress, status counts)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Should show: Team size, average progress, On Track/At Risk/Off Track counts.',
  },
  {
    question_code: 'UAT-TEAM-MEMBER-CARD',
    question_text: 'Did clicking a team member card open the Member Details dialog?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Member card click should open detailed view with KPAs, objectives, feedback, development.',
  },
  {
    question_code: 'UAT-TEAM-PENDING-ALERTS',
    question_text: 'Did pending items and alerts display correctly?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Saw pending approvals and upcoming reviews',
        'Partial - Some alerts missing',
        'No - Alerts not displaying',
        'No pending items to show'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Pending objective/IDP approvals and upcoming reviews should be indicated.',
  },
  {
    question_code: 'UAT-TEAM-FILTERS',
    question_text: 'Did status filters work (At Risk, Needs Attention)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Filters worked correctly',
        'No - Filters did not work',
        'Did not test filters'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'Filtering should show only members matching the selected status.',
  },
]

// Objective Configuration Questions (TC-031)
const OBJECTIVE_CONFIG_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-OBJ-VIEW-PENDING',
    question_text: 'Could you view pending objectives submitted by team members?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Pending objectives should be highlighted in the Objective Configuration tab.',
  },
  {
    question_code: 'UAT-OBJ-COLLAB-SESSION',
    question_text: 'Did the collaborative session workflow work (schedule, complete, then approve/reject)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Could schedule, complete, then approve/reject',
        'Partial - Session created but could not complete',
        'No - Session workflow not working',
        'Did not test collaborative session'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Approve/Reject requires completing collaborative session first.',
  },
  {
    question_code: 'UAT-OBJ-APPROVE',
    question_text: 'Could you approve an objective (after completing collaborative session)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Objective moved to Active',
        'No - Approve button was blocked',
        'No - Error during approval',
        'No pending objectives to approve'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Approved objectives should move to Active tab.',
  },
  {
    question_code: 'UAT-OBJ-REJECT',
    question_text: 'Could you reject an objective with a reason?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Rejection with reason worked',
        'No - Could not enter rejection reason',
        'No - Reject button not working',
        'Did not test rejection'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Rejection requires a reason (min 10 chars).',
  },
  {
    question_code: 'UAT-OBJ-ALLOCATE',
    question_text: 'Could you create/allocate a new objective for a team member?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Created objective with KPIs and strategic alignment',
        'Partial - Created but missing some fields',
        'No - Could not create objective',
        'Did not test allocation'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'People leader-created objectives are auto-approved but still require collaborative session.',
  },
  {
    question_code: 'UAT-OBJ-STRATEGIC-VIEW',
    question_text: 'Did the Strategic Context cascade view display correctly (L1→L2→L3→L4→L5)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Full cascade with expand/collapse worked',
        'Partial - Some levels not displaying',
        'No - Strategic view not working',
        'Did not test this view'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 6,
    helper_text: 'L1: Strategic Objective → L2: Initiative → L3: Operational → L4: Individual → L5: KPIs.',
  },
]

// Conduct Review Questions (TC-032)
const CONDUCT_REVIEW_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-CREV-INITIATE',
    question_text: 'Could you initiate a 1:1 check-in for a team member?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Conduct Check-In should open check-in workspace with KPA navigation.',
  },
  {
    question_code: 'UAT-CREV-PERIOD-FILTER',
    question_text: 'Did evidence filter correctly by check-in period?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Only saw evidence for selected period',
        'No - Evidence not filtering',
        'Could not verify filtering'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Mid-Year shows Jan-Jun evidence, Year-End shows Jul-Dec.',
  },
  {
    question_code: 'UAT-CREV-AI-TIER1',
    question_text: 'Did Tier 1 AI Review Preparation generate readiness score and insights?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Saw readiness score and focus areas',
        'Partial - Some insights but incomplete',
        'No - AI feature did not work',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 3,
    helper_text: 'Tier 1 provides overall readiness score and recommended focus areas.',
  },
  {
    question_code: 'UAT-CREV-AI-TIER2',
    question_text: 'Did Tier 2 KPA Discussion Guide provide per-KPA guidance?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Showed KPA-specific talking points',
        'Partial - Some KPAs had guidance',
        'No - Discussion guide did not appear',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Tier 2 provides discussion prompts tailored to each KPA.',
  },
  {
    question_code: 'UAT-CREV-AI-TIER3',
    question_text: 'Did Tier 3 AI Rating Assistant suggest ratings with confidence scores?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - AI suggested rating with rationale and confidence',
        'Yes - But suggestion was not helpful',
        'No - AI Rating Assistant did not work',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'Tier 3 is people leader-only and suggests Exceptional/Full/Not Yet Full/Weak.',
  },
  {
    question_code: 'UAT-CREV-AI-TIER4',
    question_text: 'Did Tier 4 Review Synthesis generate overall summary and My Personalised Growth recommendations?',
    question_type: 'multiple_choice',
    category: 'technical',
    question_config: {
      options: [
        'Yes - Generated comprehensive synthesis',
        'Partial - Summary but no My Personalised Growth suggestions',
        'No - Synthesis feature did not work',
        'Did not use this feature'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 6,
    helper_text: 'Tier 4 is people leader-only and provides overall review synthesis.',
  },
  {
    question_code: 'UAT-CREV-KPA-RATING',
    question_text: 'Could you assign ratings for each KPA (Exceptional, Full, Not Yet Full, Weak)?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 7,
    helper_text: 'Four rating options with description tooltips.',
  },
  {
    question_code: 'UAT-CREV-DISCUSSION-STEPS',
    question_text: 'Could you complete the 3-step discussion for each KPA?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 8,
    helper_text: 'Steps: What Went Well, Challenges, Actions & Development.',
  },
  {
    question_code: 'UAT-CREV-ADD-EVIDENCE',
    question_text: 'Could you add people leader evidence during the review?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Added evidence successfully',
        'No - Add Evidence button not working',
        'Did not add people leader evidence'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 9,
    helper_text: 'People leaders can add their own evidence during review.',
  },
  {
    question_code: 'UAT-CREV-COMPLETE',
    question_text: 'Could you complete and submit the review?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 10,
    helper_text: 'Review should be marked complete after submission.',
  },
  {
    question_code: 'UAT-CREV-INFORMAL',
    question_text: 'For Q1/Q3 informal check-ins, were ratings correctly NOT required?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - No rating fields for check-ins',
        'No - Rating fields appeared (should not)',
        'Did not test informal check-ins'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 11,
    helper_text: 'Q1/Q3 check-ins are informal and do not require ratings or Tier 3/4.',
  },
]

// Team Development Questions (TC-033)
const TEAM_DEVELOPMENT_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-TDEV-VIEW-PLANS',
    question_text: 'Could you view your team members\' My Personalised Growths?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'Development tab should show all direct reports with their IDP status.',
  },
  {
    question_code: 'UAT-TDEV-PENDING-ITEMS',
    question_text: 'Did you see pending items awaiting your approval?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Pending items were highlighted',
        'No - Could not identify pending items',
        'No pending items from team'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Team member-created items need people leader approval.',
  },
  {
    question_code: 'UAT-TDEV-APPROVE-ITEMS',
    question_text: 'Could you approve My Personalised Growth items (career objective, strengths, opportunities, interventions)?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Approved items in multiple sections',
        'Partial - Some sections worked',
        'No - Approval not working',
        'No items to approve'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 3,
    helper_text: 'Approve button should update item status to Approved.',
  },
  {
    question_code: 'UAT-TDEV-REJECT-REASON',
    question_text: 'Could you reject items with a reason?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Rejected with reason successfully',
        'No - Could not enter rejection reason',
        'No - Reject button not working',
        'Did not test rejection'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 4,
    helper_text: 'Rejection requires entering a reason for the team member.',
  },
  {
    question_code: 'UAT-TDEV-EDIT-ITEMS',
    question_text: 'Could you edit My Personalised Growth items as a people leader?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Edited and changes auto-approved',
        'No - Could not edit items',
        'Did not test editing'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 5,
    helper_text: 'People leader edits should auto-approve the item.',
  },
  {
    question_code: 'UAT-TDEV-ADD-ITEMS',
    question_text: 'Could you add new items (strengths, opportunities, interventions) as a people leader?',
    question_type: 'multiple_choice',
    category: 'delivery',
    question_config: {
      options: [
        'Yes - Added items marked as "People Leader Added"',
        'No - Could not add items',
        'Did not test adding items'
      ]
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 6,
    helper_text: 'People leaders can add items directly to team member plans.',
  },
  {
    question_code: 'UAT-TDEV-MANAGER-NOTES',
    question_text: 'Could you add people leader notes/feedback to items?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 7,
    helper_text: 'People leader notes are separate from team member notes.',
  },
]

// People leader setup-specific questions
const MANAGER_ORIENTATION_QUESTIONS_RAW: UATQuestionCreate[] = [
  {
    question_code: 'UAT-MGR-SLIDES-COMPLETE',
    question_text: 'Were you shown the People Leader Setup step to confirm your team?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 1,
    helper_text: 'People leaders see a team confirmation step showing direct reports.',
  },
  {
    question_code: 'UAT-MGR-TEAM-CORRECT',
    question_text: 'Was your team/direct reports list correct?',
    question_type: 'yes_no',
    category: 'delivery',
    applicable_feedback_contexts: ['project'],
    is_required: true,
    display_order: 2,
    helper_text: 'Your direct reports should be listed by name, title, and email.',
  },
  {
    question_code: 'UAT-MGR-CONTENT-HELPFUL',
    question_text: 'Was the people leader setup content helpful and informative?',
    question_type: 'rating_scale',
    category: 'communication',
    question_config: {
      min: 1,
      max: 5,
      labels: {
        '1': 'Not at all helpful',
        '2': 'Slightly helpful',
        '3': 'Moderately helpful',
        '4': 'Very helpful',
        '5': 'Extremely helpful'
      }
    },
    applicable_feedback_contexts: ['project'],
    is_required: false,
    display_order: 3,
    helper_text: 'Rate how useful the setup step was for understanding your dual platform access.',
  },
]

// Cast to QuestionCreate for API compatibility and export
export const UAT_QUESTIONS = UAT_QUESTIONS_RAW as unknown as QuestionCreate[]
export const AUTH_QUESTIONS = AUTH_QUESTIONS_RAW as unknown as QuestionCreate[]
export const ONBOARDING_QUESTIONS = ONBOARDING_QUESTIONS_RAW as unknown as QuestionCreate[]
export const MANAGER_ORIENTATION_QUESTIONS = MANAGER_ORIENTATION_QUESTIONS_RAW as unknown as QuestionCreate[]

// Team Member Journey Questions
export const OBJECTIVE_SETTING_QUESTIONS = OBJECTIVE_SETTING_QUESTIONS_RAW as unknown as QuestionCreate[]
export const VIEW_OBJECTIVES_QUESTIONS = VIEW_OBJECTIVES_QUESTIONS_RAW as unknown as QuestionCreate[]
export const ADD_EVIDENCE_QUESTIONS = ADD_EVIDENCE_QUESTIONS_RAW as unknown as QuestionCreate[]
export const PERFORMANCE_REVIEW_QUESTIONS = PERFORMANCE_REVIEW_QUESTIONS_RAW as unknown as QuestionCreate[]
export const IDP_MANAGEMENT_QUESTIONS = IDP_MANAGEMENT_QUESTIONS_RAW as unknown as QuestionCreate[]

// People Leader Journey Questions
export const TEAM_OVERVIEW_QUESTIONS = TEAM_OVERVIEW_QUESTIONS_RAW as unknown as QuestionCreate[]
export const OBJECTIVE_CONFIG_QUESTIONS = OBJECTIVE_CONFIG_QUESTIONS_RAW as unknown as QuestionCreate[]
export const CONDUCT_REVIEW_QUESTIONS = CONDUCT_REVIEW_QUESTIONS_RAW as unknown as QuestionCreate[]
export const TEAM_DEVELOPMENT_QUESTIONS = TEAM_DEVELOPMENT_QUESTIONS_RAW as unknown as QuestionCreate[]

// Export all questions combined
export const ALL_UAT_QUESTIONS: QuestionCreate[] = [
  ...UAT_QUESTIONS,
  ...AUTH_QUESTIONS,
  ...ONBOARDING_QUESTIONS,
  ...MANAGER_ORIENTATION_QUESTIONS,
  // Team Member Journey
  ...OBJECTIVE_SETTING_QUESTIONS,
  ...VIEW_OBJECTIVES_QUESTIONS,
  ...ADD_EVIDENCE_QUESTIONS,
  ...PERFORMANCE_REVIEW_QUESTIONS,
  ...IDP_MANAGEMENT_QUESTIONS,
  // People Leader Journey
  ...TEAM_OVERVIEW_QUESTIONS,
  ...OBJECTIVE_CONFIG_QUESTIONS,
  ...CONDUCT_REVIEW_QUESTIONS,
  ...TEAM_DEVELOPMENT_QUESTIONS,
]
